import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { OfficeMessagingSettings } from "../components/OfficeMessagingSettings";
import { db, functions, storage } from "../firebase";
import { DEFAULT_FEATURE_FLAGS, normalizeFeatureFlags, type FeatureFlags } from "../data/featureFlags";
import { DEFAULT_PUBLIC_WEB_MEDIA_POLICY, formatBytesMb, normalizePublicWebMediaPolicy, type PublicWebMediaPolicy } from "../utils/publicWebMediaPolicy";

type NewsPolicy = {
  defaultExpireDays: number;
  urgentDefaultHours: number;
  urgentMaxHours: number;
};

type AttachmentPolicy = {
  maxBytes: number;
  maxFilesNews: number;
  maxFilesMessaging: number;
  allowedMimeTypes: string[];
};

type EmailBranding = {
  fromName: string;
  fromEmail: string;
  replyToEmail: string;
  activationBaseUrl: string;
  adminWebBaseUrl: string;
  schoolName: string;
  appName: string;
  logoUrl: string;
  logoStoragePath: string;
  logoDownloadUrl: string;
  primaryColor: string;
  footerText: string;
  bodyFontFamily: string;
  headingFontFamily: string;
};

type EmailPreviewResponse = {
  ok: boolean;
  previewType: string;
  subject: string;
  html: string;
  text: string;
};

type RateLimitRule = {
  windowSeconds: number;
  maxAttempts: number;
};

type RateLimitPolicy = {
  interactiveInvitePerActor: RateLimitRule;
  interactiveInvitePerTargetEmail: RateLimitRule;
  passwordSetupLinkPerActor: RateLimitRule;
  passwordSetupLinkPerTargetEmail: RateLimitRule;
  linkAccountPerActor: RateLimitRule;
  roleChangePerActor: RateLimitRule;
  deleteUserPerActor: RateLimitRule;
  publicVolunteerEntryPerFamilyCode: RateLimitRule;
  publicVolunteerEntryPerEmail: RateLimitRule;
};

type RosterOptionItem = { value: string; label: string };
type RosterOptions = {
  gradeLevels: RosterOptionItem[];
  specialPrograms: RosterOptionItem[];
};

type CalendarSettings = {
  calendarApiUrl: string;
  googleCalendarId: string;
  publicCalendarUrl: string;
};

type StaffHoursDay = {
  enabled: boolean;
  start: string;
  end: string;
};

type MessagingAutoReplySettings = {
  enabled: boolean;
  timezone: string;
  messageText: string;
  showPresendDisclaimer: boolean;
  respectNoSchoolCalendar: boolean;
  noSchoolKeywords: string[];
  enabledThreadTypes: string[];
  cooldownBehavior: "offHoursWindow";
  schedule: Record<"mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun", StaffHoursDay>;
};

type ParentPrincipalMessagingSettings = {
  enabled: boolean;
  principalSpecialKey: string;
  blockedFamilyIds: string[];
  blockedStudentKeys: string[];
};

type RetentionSettings = {
  messaging: number;
  news: number;
  volunteer: number;
  messagingAttachments: number;
  newsAttachments: number;
};

type SessionPolicySettings = {
  defaultIdleTimeoutMinutes: number;
  trustedTeacherIdleTimeoutMinutes: number;
  trustedNetworkIps: string[];
  enableTrustedTeacherSessionExtension: boolean;
};
type ImportSequenceSettings = {
  nextParentNumber: number;
  nextFamilyNumber: number;
};


const DEFAULT_NEWS: NewsPolicy = {
  defaultExpireDays: 7,
  urgentDefaultHours: 24,
  urgentMaxHours: 72,
};

const DEFAULT_ATTACH: AttachmentPolicy = {
  maxBytes: 5 * 1024 * 1024,
  maxFilesNews: 3,
  maxFilesMessaging: 3,
  allowedMimeTypes: [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
  ],
};

const DEFAULT_EMAIL: EmailBranding = {
  fromName: "Zion Classical Academy",
  fromEmail: "noreply@zionclassical.org",
  replyToEmail: "office@zionclassical.org",
  activationBaseUrl: "",
  adminWebBaseUrl: "",
  schoolName: "Zion Classical Academy",
  appName: "ZCA App",
  logoUrl: "",
  logoStoragePath: "",
  logoDownloadUrl: "",
  primaryColor: "#163a5f",
  footerText: "This is an automated message from Zion Classical Academy.",
  bodyFontFamily: "Arial, Helvetica, sans-serif",
  headingFontFamily: "Arial, Helvetica, sans-serif",
};

const DEFAULT_RATE_LIMITS: RateLimitPolicy = {
  interactiveInvitePerActor: { windowSeconds: 60, maxAttempts: 10 },
  interactiveInvitePerTargetEmail: { windowSeconds: 600, maxAttempts: 3 },
  passwordSetupLinkPerActor: { windowSeconds: 60, maxAttempts: 10 },
  passwordSetupLinkPerTargetEmail: { windowSeconds: 600, maxAttempts: 3 },
  linkAccountPerActor: { windowSeconds: 60, maxAttempts: 30 },
  roleChangePerActor: { windowSeconds: 300, maxAttempts: 30 },
  deleteUserPerActor: { windowSeconds: 3600, maxAttempts: 20 },
  publicVolunteerEntryPerFamilyCode: { windowSeconds: 3600, maxAttempts: 10 },
  publicVolunteerEntryPerEmail: { windowSeconds: 3600, maxAttempts: 5 },
};

const DEFAULT_ROSTER_OPTIONS: RosterOptions = {
  gradeLevels: [
    { value: "PK", label: "Pre-K" },
    { value: "K", label: "Kindergarten" },
    { value: "1", label: "1st Grade" },
    { value: "2", label: "2nd Grade" },
    { value: "3", label: "3rd Grade" },
    { value: "4", label: "4th Grade" },
    { value: "5", label: "5th Grade" },
    { value: "6", label: "6th Grade" },
  ],
  specialPrograms: [
    { value: "art", label: "Art" },
    { value: "music", label: "Music" },
    { value: "band", label: "Band" },
    { value: "pe", label: "PE" },
    { value: "principal", label: "Principal" },
  ],
};

const DEFAULT_CALENDAR: CalendarSettings = {
  calendarApiUrl: "",
  googleCalendarId: "",
  publicCalendarUrl: "",
};

const DEFAULT_MESSAGING_AUTO_REPLY: MessagingAutoReplySettings = {
  enabled: true,
  timezone: "America/Chicago",
  messageText:
    "Thank you for your message. Staff messages are typically monitored Monday through Friday, 7:30 AM to 4:00 PM on school days. Messages sent outside those hours, on weekends, or on no-school days may not receive a response until the next school day.",
  showPresendDisclaimer: true,
  respectNoSchoolCalendar: true,
  noSchoolKeywords: ["no school", "holiday", "snow day", "school closed"],
  enabledThreadTypes: ["direct"],
  cooldownBehavior: "offHoursWindow",
  schedule: {
    mon: { enabled: true, start: "07:30", end: "16:00" },
    tue: { enabled: true, start: "07:30", end: "16:00" },
    wed: { enabled: true, start: "07:30", end: "16:00" },
    thu: { enabled: true, start: "07:30", end: "16:00" },
    fri: { enabled: true, start: "07:30", end: "16:00" },
    sat: { enabled: false, start: "07:30", end: "16:00" },
    sun: { enabled: false, start: "07:30", end: "16:00" },
  },
};

const DEFAULT_PARENT_PRINCIPAL_MESSAGING: ParentPrincipalMessagingSettings = {
  enabled: false,
  principalSpecialKey: "principal",
  blockedFamilyIds: [],
  blockedStudentKeys: [],
};

const DEFAULT_RETENTION: RetentionSettings = {
  messaging: 5,
  news: 5,
  volunteer: 7,
  messagingAttachments: 5,
  newsAttachments: 5,
};

const DEFAULT_SESSION_POLICY: SessionPolicySettings = {
  defaultIdleTimeoutMinutes: 15,
  trustedTeacherIdleTimeoutMinutes: 480,
  trustedNetworkIps: [],
  enableTrustedTeacherSessionExtension: false,
};

const DEFAULT_IMPORT_SEQUENCES: ImportSequenceSettings = {
  nextParentNumber: 1000,
  nextFamilyNumber: 1000,
};

function canonicalAdminBaseUrl(projectId: string) {
  if (projectId === "zion-classical-mobile-app") return "https://admin.zionclassical.org";
  if (projectId === "zion-classical-mobile-app-dev") return "https://devadmin.zionclassical.org";
  return projectId ? `https://${projectId}.web.app` : "";
}

function normalizeAdminBaseUrlForProject(url: string, projectId: string) {
  const normalized = String(url || "").trim().replace(/\/+$/, "");
  if (!normalized) return "";

  const isDevProject = projectId.endsWith("-dev");
  const isDevUrl = /(^https?:\/\/)?[^/]*-dev(\.|-|$)/i.test(normalized);
  const isProdAdminUrl = /(^https?:\/\/)?admin\.zionclassical\.org(\/|$)/i.test(normalized);
  const fallback = canonicalAdminBaseUrl(projectId);

  if (!isDevProject && isDevUrl) return fallback;
  if (isDevProject && isProdAdminUrl) return fallback;
  return normalized;
}



const EMAIL_FONT_OPTIONS = [
  { value: "Arial, Helvetica, sans-serif", label: "Arial" },
  { value: "Helvetica, Arial, sans-serif", label: "Helvetica" },
  { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
  { value: "Trebuchet MS, Arial, sans-serif", label: "Trebuchet MS" },
  { value: "Tahoma, Arial, sans-serif", label: "Tahoma" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "Times New Roman, Times, serif", label: "Times New Roman" },
];

const COMMON_MIME_TYPES = [
  { value: "application/pdf", label: "PDF" },
  { value: "image/jpeg", label: "JPEG" },
  { value: "image/png", label: "PNG" },
  { value: "image/webp", label: "WEBP" },
  {
    value: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    label: "DOCX",
  },
  { value: "application/msword", label: "DOC" },
];

function num(v: any, fallback: number) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function asString(v: any, fallback = "") {
  return typeof v === "string" ? v : fallback;
}

function normalizeKey(v: string) {
  return v.trim();
}

function normalizeLabel(v: string) {
  return v.trim();
}

function dedupeStrings(values: string[]) {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of values.map((v) => v.trim()).filter(Boolean)) {
    const key = item.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

function sanitizeRosterItems(items: RosterOptionItem[]) {
  const seen = new Set<string>();
  const out: RosterOptionItem[] = [];
  for (const item of items) {
    const value = normalizeKey(item.value);
    const label = normalizeLabel(item.label) || value;
    if (!value) continue;
    const dedupe = value.toLowerCase();
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    out.push({ value, label });
  }
  return out;
}

function readRosterItems(src: any, fallback: RosterOptionItem[]) {
  if (!Array.isArray(src)) return fallback;
  const mapped = src.map((item: any) => {
    if (typeof item === "string") {
      const value = normalizeKey(item);
      return { value, label: value };
    }
    const value = normalizeKey(asString(item?.value));
    const label = normalizeLabel(asString(item?.label)) || value;
    return { value, label };
  });
  const clean = sanitizeRosterItems(mapped);
  return clean.length ? clean : fallback;
}

function ensurePrincipalSpecialProgram(items: RosterOptionItem[]) {
  if (items.some((item) => item.value.trim().toLowerCase() === "principal")) return items;
  return [...items, { value: "principal", label: "Principal" }];
}

function readParentPrincipalMessagingSettings(src: any): ParentPrincipalMessagingSettings {
  const specialKey = asString(src?.principalSpecialKey, DEFAULT_PARENT_PRINCIPAL_MESSAGING.principalSpecialKey).trim().toLowerCase();
  return {
    enabled: src?.enabled === true,
    principalSpecialKey: specialKey || DEFAULT_PARENT_PRINCIPAL_MESSAGING.principalSpecialKey,
    blockedFamilyIds: Array.isArray(src?.blockedFamilyIds)
      ? dedupeStrings(src.blockedFamilyIds.map(String))
      : DEFAULT_PARENT_PRINCIPAL_MESSAGING.blockedFamilyIds,
    blockedStudentKeys: Array.isArray(src?.blockedStudentKeys)
      ? dedupeStrings(src.blockedStudentKeys.map(String))
      : DEFAULT_PARENT_PRINCIPAL_MESSAGING.blockedStudentKeys,
  };
}

function readRule(src: any, fallback: RateLimitRule): RateLimitRule {
  return {
    windowSeconds: Math.max(1, Math.min(86400, num(src?.windowSeconds, fallback.windowSeconds))),
    maxAttempts: Math.max(1, Math.min(10000, num(src?.maxAttempts, fallback.maxAttempts))),
  };
}

function normalizeTime(value: any, fallback: string) {
  const raw = asString(value, fallback).trim();
  const match = /^(\d{1,2}):(\d{2})$/.exec(raw);
  if (!match) return fallback;
  const hour = Math.max(0, Math.min(23, Number(match[1])));
  const minute = Math.max(0, Math.min(59, Number(match[2])));
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function readStaffHoursDay(src: any, fallback: StaffHoursDay): StaffHoursDay {
  return {
    enabled: src?.enabled === undefined ? fallback.enabled : src.enabled === true,
    start: normalizeTime(src?.start, fallback.start),
    end: normalizeTime(src?.end, fallback.end),
  };
}

function readMessagingAutoReplySettings(src: any): MessagingAutoReplySettings {
  const fallback = DEFAULT_MESSAGING_AUTO_REPLY;
  return {
    enabled: src?.enabled === undefined ? fallback.enabled : src.enabled === true,
    timezone: asString(src?.timezone, fallback.timezone).trim() || fallback.timezone,
    messageText: asString(src?.messageText, fallback.messageText).trim() || fallback.messageText,
    showPresendDisclaimer:
      src?.showPresendDisclaimer === undefined ? fallback.showPresendDisclaimer : src.showPresendDisclaimer === true,
    respectNoSchoolCalendar:
      src?.respectNoSchoolCalendar === undefined ? fallback.respectNoSchoolCalendar : src.respectNoSchoolCalendar === true,
    noSchoolKeywords: Array.isArray(src?.noSchoolKeywords)
      ? dedupeStrings(src.noSchoolKeywords.map(String))
      : fallback.noSchoolKeywords,
    enabledThreadTypes: Array.isArray(src?.enabledThreadTypes)
      ? dedupeStrings(src.enabledThreadTypes.map(String))
      : fallback.enabledThreadTypes,
    cooldownBehavior: "offHoursWindow",
    schedule: {
      mon: readStaffHoursDay(src?.schedule?.mon, fallback.schedule.mon),
      tue: readStaffHoursDay(src?.schedule?.tue, fallback.schedule.tue),
      wed: readStaffHoursDay(src?.schedule?.wed, fallback.schedule.wed),
      thu: readStaffHoursDay(src?.schedule?.thu, fallback.schedule.thu),
      fri: readStaffHoursDay(src?.schedule?.fri, fallback.schedule.fri),
      sat: readStaffHoursDay(src?.schedule?.sat, fallback.schedule.sat),
      sun: readStaffHoursDay(src?.schedule?.sun, fallback.schedule.sun),
    },
  };
}

function clampYears(v: any, fallback: number) {
  return Math.max(1, Math.min(25, num(v, fallback)));
}

function sanitizeEmailFont(value: any, fallback: string) {
  const candidate = asString(value, fallback);
  return EMAIL_FONT_OPTIONS.some((option) => option.value === candidate) ? candidate : fallback;
}

function sanitizeLogoFileName(name: string) {
  return name.replace(/[^a-zA-Z0-9._-]+/g, "-");
}

function RateLimitEditor(props: {
  label: string;
  value: RateLimitRule;
  onChange: (next: RateLimitRule) => void;
}) {
  return (
    <div style={{ display: "grid", gap: 8, padding: 12, border: "1px solid #eee", borderRadius: 12 }}>
      <div style={{ fontWeight: 700 }}>{props.label}</div>
      <div className="grid2">
        <label className="muted" style={{ display: "grid", gap: 6 }}>
          Window seconds
          <input
            className="input"
            type="number"
            min={1}
            max={86400}
            value={props.value.windowSeconds}
            onChange={(e) =>
              props.onChange({
                ...props.value,
                windowSeconds: Math.max(1, Math.min(86400, num(e.target.value, props.value.windowSeconds))),
              })
            }
          />
        </label>

        <label className="muted" style={{ display: "grid", gap: 6 }}>
          Max attempts
          <input
            className="input"
            type="number"
            min={1}
            max={10000}
            value={props.value.maxAttempts}
            onChange={(e) =>
              props.onChange({
                ...props.value,
                maxAttempts: Math.max(1, Math.min(10000, num(e.target.value, props.value.maxAttempts))),
              })
            }
          />
        </label>
      </div>
    </div>
  );
}

function SimpleListEditor(props: {
  title: string;
  description?: string;
  values: string[];
  onChange: (next: string[]) => void;
  addLabel?: string;
  placeholder?: string;
  quickAdds?: Array<{ label: string; value: string }>;
}) {
  const { values, onChange } = props;

  function updateValue(index: number, value: string) {
    const next = [...values];
    next[index] = value;
    onChange(next);
  }

  function removeValue(index: number) {
    const next = [...values];
    next.splice(index, 1);
    onChange(next);
  }

  function addBlank() {
    onChange([...values, ""]);
  }

  function addQuick(value: string) {
    if (values.some((v) => v.trim().toLowerCase() === value.trim().toLowerCase())) return;
    onChange([...values, value]);
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div>
        <div style={{ fontWeight: 700 }}>{props.title}</div>
        {props.description ? <div className="muted" style={{ marginTop: 4 }}>{props.description}</div> : null}
      </div>

      {props.quickAdds?.length ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {props.quickAdds.map((item) => (
            <button
              key={item.value}
              type="button"
              className="btn secondary"
              onClick={() => addQuick(item.value)}
            >
              Add {item.label}
            </button>
          ))}
        </div>
      ) : null}

      <div style={{ display: "grid", gap: 8 }}>
        {values.length === 0 ? <div className="muted">No entries yet.</div> : null}
        {values.map((value, index) => (
          <div key={index} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center" }}>
            <input
              className="input"
              value={value}
              placeholder={props.placeholder}
              onChange={(e) => updateValue(index, e.target.value)}
            />
            <button type="button" className="btn secondary" onClick={() => removeValue(index)}>
              Remove
            </button>
          </div>
        ))}
      </div>

      <div>
        <button type="button" className="btn secondary" onClick={addBlank}>
          {props.addLabel || "Add Entry"}
        </button>
      </div>
    </div>
  );
}

function RosterOptionsEditor(props: {
  title: string;
  description?: string;
  values: RosterOptionItem[];
  onChange: (next: RosterOptionItem[]) => void;
  addLabel: string;
}) {
  const { values, onChange } = props;

  function update(index: number, field: keyof RosterOptionItem, value: string) {
    const next = [...values];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  }

  function addRow() {
    onChange([...values, { value: "", label: "" }]);
  }

  function removeRow(index: number) {
    const next = [...values];
    next.splice(index, 1);
    onChange(next);
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div>
        <div style={{ fontWeight: 700 }}>{props.title}</div>
        {props.description ? <div className="muted" style={{ marginTop: 4 }}>{props.description}</div> : null}
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {values.length === 0 ? <div className="muted">No entries yet.</div> : null}
        {values.map((item, index) => (
          <div key={index} style={{ display: "grid", gridTemplateColumns: "180px 1fr auto", gap: 8, alignItems: "center" }}>
            <input
              className="input"
              value={item.value}
              placeholder="Value"
              onChange={(e) => update(index, "value", e.target.value)}
            />
            <input
              className="input"
              value={item.label}
              placeholder="Label"
              onChange={(e) => update(index, "label", e.target.value)}
            />
            <button type="button" className="btn secondary" onClick={() => removeRow(index)}>
              Remove
            </button>
          </div>
        ))}
      </div>

      <div>
        <button type="button" className="btn secondary" onClick={addRow}>{props.addLabel}</button>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const projectId = String(import.meta.env.VITE_FIREBASE_PROJECT_ID || "").trim();
  const adminBaseUrlPlaceholder = canonicalAdminBaseUrl(projectId);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [news, setNews] = useState<NewsPolicy>(DEFAULT_NEWS);
  const [attach, setAttach] = useState<AttachmentPolicy>(DEFAULT_ATTACH);
  const [emailBranding, setEmailBranding] = useState<EmailBranding>(DEFAULT_EMAIL);
  const [emailSaving, setEmailSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [logoUiError, setLogoUiError] = useState<string | null>(null);
  const [logoUiSuccess, setLogoUiSuccess] = useState<string | null>(null);
  const [logoLocalPreviewUrl, setLogoLocalPreviewUrl] = useState<string>("");
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const [rateLimits, setRateLimits] = useState<RateLimitPolicy>(DEFAULT_RATE_LIMITS);
  const [rosterOptions, setRosterOptions] = useState<RosterOptions>(DEFAULT_ROSTER_OPTIONS);
  const [calendarSettings, setCalendarSettings] = useState<CalendarSettings>(DEFAULT_CALENDAR);
  const [messagingAutoReply, setMessagingAutoReply] = useState<MessagingAutoReplySettings>(DEFAULT_MESSAGING_AUTO_REPLY);
  const [parentPrincipalMessaging, setParentPrincipalMessaging] = useState<ParentPrincipalMessagingSettings>(DEFAULT_PARENT_PRINCIPAL_MESSAGING);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const [retentionSettings, setRetentionSettings] = useState<RetentionSettings>(DEFAULT_RETENTION);
  const [sessionPolicy, setSessionPolicy] = useState<SessionPolicySettings>(DEFAULT_SESSION_POLICY);
  const [importSequences, setImportSequences] = useState<ImportSequenceSettings>(DEFAULT_IMPORT_SEQUENCES);
  const [publicWebMediaPolicy, setPublicWebMediaPolicy] = useState<PublicWebMediaPolicy>(DEFAULT_PUBLIC_WEB_MEDIA_POLICY);

  const previewEmailTemplate = useMemo(() => httpsCallable<any, EmailPreviewResponse>(functions, "previewSystemEmailTemplate"), []);

  useEffect(() => {
    if (!user?.uid) return;

    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const newsSnap = await getDoc(doc(db, "settings", "newsPolicy"));
        if (newsSnap.exists()) {
          const d: any = newsSnap.data();
          setNews({
            defaultExpireDays: Math.max(1, Math.min(365, num(d.defaultExpireDays, DEFAULT_NEWS.defaultExpireDays))),
            urgentDefaultHours: Math.max(1, Math.min(168, num(d.urgentDefaultHours, DEFAULT_NEWS.urgentDefaultHours))),
            urgentMaxHours: Math.max(1, Math.min(720, num(d.urgentMaxHours, DEFAULT_NEWS.urgentMaxHours))),
          });
        }

        const attSnap = await getDoc(doc(db, "settings", "attachmentPolicy"));
        if (attSnap.exists()) {
          const d: any = attSnap.data();
          const storedAllowed =
            Array.isArray(d.allowedMimeTypes)
              ? d.allowedMimeTypes.map(String)
              : Array.isArray(d.allowedContentTypes)
              ? d.allowedContentTypes.map(String)
              : DEFAULT_ATTACH.allowedMimeTypes;
          const allowed = dedupeStrings([...storedAllowed, ...DEFAULT_ATTACH.allowedMimeTypes]);

          setAttach({
            maxBytes: Math.max(
              256 * 1024,
              Math.min(50 * 1024 * 1024, num(d.maxBytesNews ?? d.maxBytes ?? d.maxBytesMessaging, DEFAULT_ATTACH.maxBytes))
            ),
            maxFilesNews: Math.max(
              0,
              Math.min(10, num(d.maxFilesPerNewsPost ?? d.maxFilesNews ?? d.maxFilesPerNews, DEFAULT_ATTACH.maxFilesNews))
            ),
            maxFilesMessaging: Math.max(
              0,
              Math.min(10, num(d.maxFilesMessaging ?? d.maxFilesPerMessage, DEFAULT_ATTACH.maxFilesMessaging))
            ),
            allowedMimeTypes: allowed,
          });
        }

        const publicWebMediaSnap = await getDoc(doc(db, "settings", "publicWebMediaPolicy"));
        setPublicWebMediaPolicy(normalizePublicWebMediaPolicy(publicWebMediaSnap.exists() ? publicWebMediaSnap.data() : {}));

        const rosterSnap = await getDoc(doc(db, "settings", "rosterOptions"));
        if (rosterSnap.exists()) {
          const d: any = rosterSnap.data();
          setRosterOptions({
            gradeLevels: readRosterItems(d.gradeLevels, DEFAULT_ROSTER_OPTIONS.gradeLevels),
            specialPrograms: ensurePrincipalSpecialProgram(readRosterItems(d.specialPrograms, DEFAULT_ROSTER_OPTIONS.specialPrograms)),
          });
        }


        const calendarSnap = await getDoc(doc(db, "settings", "calendar"));
        if (calendarSnap.exists()) {
          const d: any = calendarSnap.data();
          setCalendarSettings({
            calendarApiUrl: asString(d.calendarApiUrl ?? d.apiUrl, DEFAULT_CALENDAR.calendarApiUrl),
            googleCalendarId: asString(d.googleCalendarId, DEFAULT_CALENDAR.googleCalendarId),
            publicCalendarUrl: asString(d.publicCalendarUrl, DEFAULT_CALENDAR.publicCalendarUrl),
          });
        }

        const messagingAutoReplySnap = await getDoc(doc(db, "settings", "messagingAutoReply"));
        setMessagingAutoReply(readMessagingAutoReplySettings(messagingAutoReplySnap.exists() ? messagingAutoReplySnap.data() : {}));

        const parentPrincipalMessagingSnap = await getDoc(doc(db, "settings", "parentPrincipalMessaging"));
        setParentPrincipalMessaging(
          readParentPrincipalMessagingSettings(parentPrincipalMessagingSnap.exists() ? parentPrincipalMessagingSnap.data() : {})
        );

        const featureSnap = await getDoc(doc(db, "settings", "featureFlags"));
        setFeatureFlags(normalizeFeatureFlags(featureSnap.exists() ? featureSnap.data() : {}));

        const schoolYearSnap = await getDoc(doc(db, "settings", "schoolYear"));
        if (schoolYearSnap.exists()) {
          const d: any = schoolYearSnap.data();
          const r: any = d.retentionYears ?? {};
          setRetentionSettings({
            messaging: clampYears(r.messaging, DEFAULT_RETENTION.messaging),
            news: clampYears(r.news, DEFAULT_RETENTION.news),
            volunteer: clampYears(r.volunteer, DEFAULT_RETENTION.volunteer),
            messagingAttachments: clampYears(r.messagingAttachments, DEFAULT_RETENTION.messagingAttachments),
            newsAttachments: clampYears(r.newsAttachments, DEFAULT_RETENTION.newsAttachments),
          });
        }

        const emailSnap = await getDoc(doc(db, "settings", "email"));
        if (emailSnap.exists()) {
          const d: any = emailSnap.data();
          const loadedEmailBranding: EmailBranding = {
            fromName: asString(d.fromName, DEFAULT_EMAIL.fromName),
            fromEmail: asString(d.fromEmail, DEFAULT_EMAIL.fromEmail),
            replyToEmail: asString(d.replyToEmail, DEFAULT_EMAIL.replyToEmail),
            activationBaseUrl: asString(d.activationBaseUrl, DEFAULT_EMAIL.activationBaseUrl),
            adminWebBaseUrl: asString(d.adminWebBaseUrl, DEFAULT_EMAIL.adminWebBaseUrl),
            schoolName: asString(d.schoolName, DEFAULT_EMAIL.schoolName),
            appName: asString(d.appName, DEFAULT_EMAIL.appName),
            logoUrl: asString(d.logoDownloadUrl ?? d.logoUrl, DEFAULT_EMAIL.logoUrl),
            logoStoragePath: asString(d.logoStoragePath, DEFAULT_EMAIL.logoStoragePath),
            logoDownloadUrl: asString(d.logoDownloadUrl ?? d.logoUrl, DEFAULT_EMAIL.logoDownloadUrl),
            primaryColor: asString(d.primaryColor, DEFAULT_EMAIL.primaryColor),
            footerText: asString(d.footerText, DEFAULT_EMAIL.footerText),
            bodyFontFamily: sanitizeEmailFont(d.bodyFontFamily, DEFAULT_EMAIL.bodyFontFamily),
            headingFontFamily: sanitizeEmailFont(d.headingFontFamily, DEFAULT_EMAIL.headingFontFamily),
          };
          setEmailBranding(loadedEmailBranding);
          await refreshEmailPreview(loadedEmailBranding);
        }

        const rateSnap = await getDoc(doc(db, "settings", "rateLimitPolicy"));
        if (rateSnap.exists()) {
          const d: any = rateSnap.data();
          setRateLimits({
            interactiveInvitePerActor: readRule(d.interactiveInvitePerActor, DEFAULT_RATE_LIMITS.interactiveInvitePerActor),
            interactiveInvitePerTargetEmail: readRule(d.interactiveInvitePerTargetEmail, DEFAULT_RATE_LIMITS.interactiveInvitePerTargetEmail),
            passwordSetupLinkPerActor: readRule(d.passwordSetupLinkPerActor, DEFAULT_RATE_LIMITS.passwordSetupLinkPerActor),
            passwordSetupLinkPerTargetEmail: readRule(d.passwordSetupLinkPerTargetEmail, DEFAULT_RATE_LIMITS.passwordSetupLinkPerTargetEmail),
            linkAccountPerActor: readRule(d.linkAccountPerActor, DEFAULT_RATE_LIMITS.linkAccountPerActor),
            roleChangePerActor: readRule(d.roleChangePerActor, DEFAULT_RATE_LIMITS.roleChangePerActor),
            deleteUserPerActor: readRule(d.deleteUserPerActor, DEFAULT_RATE_LIMITS.deleteUserPerActor),
            publicVolunteerEntryPerFamilyCode: readRule(d.publicVolunteerEntryPerFamilyCode, DEFAULT_RATE_LIMITS.publicVolunteerEntryPerFamilyCode),
            publicVolunteerEntryPerEmail: readRule(d.publicVolunteerEntryPerEmail, DEFAULT_RATE_LIMITS.publicVolunteerEntryPerEmail),
          });
        }

        const sessionSnap = await getDoc(doc(db, "settings", "sessionPolicy"));
        if (sessionSnap.exists()) {
          const d: any = sessionSnap.data();
          setSessionPolicy({
            defaultIdleTimeoutMinutes: Math.max(1, Math.min(24 * 60, num(d.defaultIdleTimeoutMinutes, DEFAULT_SESSION_POLICY.defaultIdleTimeoutMinutes))),
            trustedTeacherIdleTimeoutMinutes: Math.max(1, Math.min(24 * 60, num(d.trustedTeacherIdleTimeoutMinutes, DEFAULT_SESSION_POLICY.trustedTeacherIdleTimeoutMinutes))),
            trustedNetworkIps: Array.isArray(d.trustedNetworkIps)
              ? dedupeStrings(d.trustedNetworkIps.map(String))
              : DEFAULT_SESSION_POLICY.trustedNetworkIps,
            enableTrustedTeacherSessionExtension: d.enableTrustedTeacherSessionExtension === true,
          });
        }

        const seqSnap = await getDoc(doc(db, "settings", "importSequences"));
        if (seqSnap.exists()) {
          const d: any = seqSnap.data();
          setImportSequences({
            nextParentNumber: Math.max(1, num(d.nextParentNumber, DEFAULT_IMPORT_SEQUENCES.nextParentNumber)),
            nextFamilyNumber: Math.max(1, num(d.nextFamilyNumber, DEFAULT_IMPORT_SEQUENCES.nextFamilyNumber)),
          });
        }
      } catch (e: any) {
        console.error("[SettingsPage] load failed", e);
        setErr(e?.message || "Failed to load settings.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    void refreshEmailPreview();
  }, [user?.uid]);

  async function saveNews() {
    setErr(null);
    try {
      await setDoc(
        doc(db, "settings", "newsPolicy"),
        {
          ...news,
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      alert("Saved news policy.");
    } catch (e: any) {
      console.error("[SettingsPage] saveNews failed", e);
      setErr(e?.message || "Failed to save news policy.");
    }
  }

  async function saveAttach() {
    setErr(null);
    try {
      const allowed = dedupeStrings(attach.allowedMimeTypes);
      await setDoc(
        doc(db, "settings", "attachmentPolicy"),
        {
          maxBytes: attach.maxBytes,
          maxBytesNews: attach.maxBytes,
          maxBytesMessaging: attach.maxBytes,
          maxFilesNews: attach.maxFilesNews,
          maxFilesPerNewsPost: attach.maxFilesNews,
          maxFilesPerNews: attach.maxFilesNews,
          maxFilesMessaging: attach.maxFilesMessaging,
          maxFilesPerMessage: attach.maxFilesMessaging,
          allowedMimeTypes: allowed,
          allowedContentTypes: allowed,
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      setAttach((prev) => ({ ...prev, allowedMimeTypes: allowed }));
      alert("Saved attachment policy.");
    } catch (e: any) {
      console.error("[SettingsPage] saveAttach failed", e);
      setErr(e?.message || "Failed to save attachment policy.");
    }
  }

  async function savePublicWebMediaPolicy() {
    setErr(null);
    if (!isSuperAdmin) {
      setErr("Only Super Admin may modify public website media upload limits.");
      return;
    }
    try {
      const normalized = normalizePublicWebMediaPolicy(publicWebMediaPolicy);
      await setDoc(
        doc(db, "settings", "publicWebMediaPolicy"),
        {
          ...normalized,
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
          updatedByEmail: user?.email ?? null,
        },
        { merge: true }
      );
      setPublicWebMediaPolicy(normalized);
      alert("Saved public website media limits.");
    } catch (e: any) {
      console.error("[SettingsPage] savePublicWebMediaPolicy failed", e);
      setErr(e?.message || "Failed to save public website media limits.");
    }
  }

  async function saveRosterOptions() {
    setErr(null);
    try {
      const gradeLevels = sanitizeRosterItems(rosterOptions.gradeLevels);
      const specialPrograms = ensurePrincipalSpecialProgram(sanitizeRosterItems(rosterOptions.specialPrograms));
      await setDoc(
        doc(db, "settings", "rosterOptions"),
        {
          gradeLevels,
          specialPrograms,
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      setRosterOptions({ gradeLevels, specialPrograms });
      alert("Saved roster options.");
    } catch (e: any) {
      console.error("[SettingsPage] saveRosterOptions failed", e);
      setErr(e?.message || "Failed to save roster options.");
    }
  }


  async function saveCalendarSettings() {
    setErr(null);
    try {
      await setDoc(
        doc(db, "settings", "calendar"),
        {
          calendarApiUrl: calendarSettings.calendarApiUrl.trim(),
          apiUrl: calendarSettings.calendarApiUrl.trim(),
          googleCalendarId: calendarSettings.googleCalendarId.trim(),
          publicCalendarUrl: calendarSettings.publicCalendarUrl.trim(),
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      alert("Saved calendar settings.");
    } catch (e: any) {
      console.error("[SettingsPage] saveCalendarSettings failed", e);
      setErr(e?.message || "Failed to save calendar settings.");
    }
  }

  function updateMessagingAutoReplyDay(day: keyof MessagingAutoReplySettings["schedule"], patch: Partial<StaffHoursDay>) {
    setMessagingAutoReply((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          ...patch,
        },
      },
    }));
  }

  function toggleMessagingAutoReplyThreadType(threadType: string, enabled: boolean) {
    setMessagingAutoReply((prev) => {
      const current = new Set(prev.enabledThreadTypes);
      if (enabled) current.add(threadType);
      else current.delete(threadType);
      return { ...prev, enabledThreadTypes: Array.from(current) };
    });
  }

  async function saveMessagingAutoReplySettings() {
    if (!isAdmin && !isSuperAdmin) {
      setErr("Only Admin or Super Admin may change messaging auto-reply settings.");
      return;
    }

    setErr(null);
    try {
      const next = readMessagingAutoReplySettings(messagingAutoReply);
      await setDoc(
        doc(db, "settings", "messagingAutoReply"),
        {
          ...next,
          settingsVersion: new Date().toISOString(),
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      setMessagingAutoReply(next);
      alert("Saved messaging auto-reply settings.");
    } catch (e: any) {
      console.error("[SettingsPage] saveMessagingAutoReplySettings failed", e);
      setErr(e?.message || "Failed to save messaging auto-reply settings.");
    }
  }

  async function saveParentPrincipalMessagingSettings() {
    if (!isAdmin && !isSuperAdmin) {
      setErr("Only Admin or Super Admin may change parent-to-principal messaging settings.");
      return;
    }

    setErr(null);
    try {
      const next = readParentPrincipalMessagingSettings(parentPrincipalMessaging);
      await setDoc(
        doc(db, "settings", "parentPrincipalMessaging"),
        {
          ...next,
          settingsVersion: new Date().toISOString(),
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      setParentPrincipalMessaging(next);
      alert("Saved parent-to-principal messaging settings.");
    } catch (e: any) {
      console.error("[SettingsPage] saveParentPrincipalMessagingSettings failed", e);
      setErr(e?.message || "Failed to save parent-to-principal messaging settings.");
    }
  }

  async function saveFeatureFlags() {
    if (!isAdmin && !isSuperAdmin) {
      setErr("Only Admin or Super Admin may change feature availability.");
      return;
    }
    setErr(null);
    try {
      await setDoc(
        doc(db, "settings", "featureFlags"),
        {
          familyCrmEnabled: featureFlags.familyCrmEnabled,
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      alert("Saved feature availability.");
    } catch (e: any) {
      console.error("[SettingsPage] saveFeatureFlags failed", e);
      setErr(e?.message || "Failed to save feature availability.");
    }
  }

  async function saveRetentionSettings() {
    setErr(null);
    try {
      const next = {
        messaging: clampYears(retentionSettings.messaging, DEFAULT_RETENTION.messaging),
        news: clampYears(retentionSettings.news, DEFAULT_RETENTION.news),
        volunteer: clampYears(retentionSettings.volunteer, DEFAULT_RETENTION.volunteer),
        messagingAttachments: clampYears(retentionSettings.messagingAttachments, DEFAULT_RETENTION.messagingAttachments),
        newsAttachments: clampYears(retentionSettings.newsAttachments, DEFAULT_RETENTION.newsAttachments),
      };

      await setDoc(
        doc(db, "settings", "schoolYear"),
        {
          retentionYears: next,
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      setRetentionSettings(next);
      alert("Saved retention settings.");
    } catch (e: any) {
      console.error("[SettingsPage] saveRetentionSettings failed", e);
      setErr(e?.message || "Failed to save retention settings.");
    }
  }

  async function refreshEmailPreview(nextBranding?: EmailBranding) {
    setErr(null);
    setPreviewLoading(true);
    try {
      const activeBranding = nextBranding ?? emailBranding;
      const result = await previewEmailTemplate({
        previewType: "passwordSetup",
        settings: {
          fromName: activeBranding.fromName,
          fromEmail: activeBranding.fromEmail,
          replyToEmail: activeBranding.replyToEmail,
          schoolName: activeBranding.schoolName,
          appName: activeBranding.appName,
          logoUrl: activeBranding.logoDownloadUrl || activeBranding.logoUrl,
          logoDownloadUrl: activeBranding.logoDownloadUrl || activeBranding.logoUrl,
          logoStoragePath: activeBranding.logoStoragePath,
          primaryColor: activeBranding.primaryColor,
          footerText: activeBranding.footerText,
          bodyFontFamily: activeBranding.bodyFontFamily,
          headingFontFamily: activeBranding.headingFontFamily,
        },
      });
      const data = result.data as EmailPreviewResponse;
      setPreviewHtml(data.html || "");
      setPreviewSubject(data.subject || "");
    } catch (e: any) {
      console.error("[SettingsPage] refreshEmailPreview failed", e);
      setErr(e?.message || "Failed to generate email preview.");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function uploadEmailLogo(file: File) {
    setErr(null);
    setLogoUiError(null);
    setLogoUiSuccess(null);

    const allowed = ["image/png", "image/jpeg", "image/webp"];
    if (!allowed.includes(file.type)) {
      const msg = "Logo must be PNG, JPEG, or WEBP.";
      setErr(msg);
      setLogoUiError(msg);
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      const msg = "Logo must be 2 MB or smaller.";
      setErr(msg);
      setLogoUiError(msg);
      return;
    }

    const localPreviewUrl = URL.createObjectURL(file);
    setLogoLocalPreviewUrl(localPreviewUrl);
    setLogoUploading(true);
    try {
      const safeName = sanitizeLogoFileName(file.name || `logo-${Date.now()}`);
      const path = `settings/email/${Date.now()}-${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const downloadUrl = await getDownloadURL(storageRef);
      const nextBranding: EmailBranding = {
        ...emailBranding,
        logoStoragePath: path,
        logoDownloadUrl: downloadUrl,
        logoUrl: downloadUrl,
      };
      setEmailBranding(nextBranding);
      setLogoUiSuccess("Logo uploaded. Save Email Branding to keep it in settings.");
      await refreshEmailPreview(nextBranding);
    } catch (e: any) {
      console.error("[SettingsPage] uploadEmailLogo failed", e);
      const msg = e?.message || "Failed to upload logo.";
      setErr(msg);
      setLogoUiError(msg);
      setLogoLocalPreviewUrl("");
    } finally {
      setLogoUploading(false);
      URL.revokeObjectURL(localPreviewUrl);
    }
  }

  async function removeEmailLogo() {
    setErr(null);
    setLogoUiError(null);
    setLogoUiSuccess(null);
    setLogoLocalPreviewUrl("");
    try {
      if (emailBranding.logoStoragePath) {
        await deleteObject(ref(storage, emailBranding.logoStoragePath)).catch(() => undefined);
      }
      const nextBranding: EmailBranding = {
        ...emailBranding,
        logoStoragePath: "",
        logoDownloadUrl: "",
        logoUrl: "",
      };
      setEmailBranding(nextBranding);
      await refreshEmailPreview(nextBranding);
    } catch (e: any) {
      console.error("[SettingsPage] removeEmailLogo failed", e);
      setErr(e?.message || "Failed to remove logo.");
    }
  }

  async function saveEmailBranding() {
    setErr(null);
    setEmailSaving(true);
    try {
      const activationBaseUrl = normalizeAdminBaseUrlForProject(emailBranding.activationBaseUrl, projectId);
      const adminWebBaseUrl = normalizeAdminBaseUrlForProject(emailBranding.adminWebBaseUrl, projectId);
      await setDoc(
        doc(db, "settings", "email"),
        {
          fromName: emailBranding.fromName.trim(),
          fromEmail: emailBranding.fromEmail.trim(),
          replyToEmail: emailBranding.replyToEmail.trim(),
          activationBaseUrl,
          adminWebBaseUrl,
          schoolName: emailBranding.schoolName.trim(),
          appName: emailBranding.appName.trim(),
          logoStoragePath: emailBranding.logoStoragePath,
          logoDownloadUrl: emailBranding.logoDownloadUrl || emailBranding.logoUrl,
          logoUrl: emailBranding.logoDownloadUrl || emailBranding.logoUrl,
          primaryColor: emailBranding.primaryColor.trim(),
          footerText: emailBranding.footerText.trim(),
          bodyFontFamily: sanitizeEmailFont(emailBranding.bodyFontFamily, DEFAULT_EMAIL.bodyFontFamily),
          headingFontFamily: sanitizeEmailFont(emailBranding.headingFontFamily, DEFAULT_EMAIL.headingFontFamily),
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      setEmailBranding((prev) => ({
        ...prev,
        activationBaseUrl,
        adminWebBaseUrl,
      }));
      await refreshEmailPreview();
      alert("Saved email branding.");
    } catch (e: any) {
      console.error("[SettingsPage] saveEmailBranding failed", e);
      setErr(e?.message || "Failed to save email branding.");
    } finally {
      setEmailSaving(false);
    }
  }

  async function saveImportSequences() {
    setErr(null);
    try {
      await setDoc(
        doc(db, "settings", "importSequences"),
        {
          nextParentNumber: Math.max(1, Math.floor(num(importSequences.nextParentNumber, DEFAULT_IMPORT_SEQUENCES.nextParentNumber))),
          nextFamilyNumber: Math.max(1, Math.floor(num(importSequences.nextFamilyNumber, DEFAULT_IMPORT_SEQUENCES.nextFamilyNumber))),
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
          updatedByEmail: user?.email ?? null,
        },
        { merge: true }
      );
      alert("Import sequence settings saved.");
    } catch (e: any) {
      setErr(e?.message || "Failed to save import sequence settings.");
    }
  }

  async function saveSessionPolicy() {
    if (!isSuperAdmin) {
      setErr("Only Super Admin may modify session policy settings.");
      return;
    }

    setErr(null);
    try {
      const next = {
        defaultIdleTimeoutMinutes: Math.max(1, Math.min(24 * 60, num(sessionPolicy.defaultIdleTimeoutMinutes, DEFAULT_SESSION_POLICY.defaultIdleTimeoutMinutes))),
        trustedTeacherIdleTimeoutMinutes: Math.max(1, Math.min(24 * 60, num(sessionPolicy.trustedTeacherIdleTimeoutMinutes, DEFAULT_SESSION_POLICY.trustedTeacherIdleTimeoutMinutes))),
        trustedNetworkIps: dedupeStrings(sessionPolicy.trustedNetworkIps),
        enableTrustedTeacherSessionExtension: sessionPolicy.enableTrustedTeacherSessionExtension === true,
      };

      await setDoc(
        doc(db, "settings", "sessionPolicy"),
        {
          ...next,
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      setSessionPolicy(next);
      alert("Saved session policy.");
    } catch (e: any) {
      console.error("[SettingsPage] saveSessionPolicy failed", e);
      setErr(e?.message || "Failed to save session policy.");
    }
  }

  async function saveRateLimits() {
    if (!isSuperAdmin) {
      setErr("Only Super Admin may modify rate limit settings.");
      return;
    }

    setErr(null);
    try {
      await setDoc(
        doc(db, "settings", "rateLimitPolicy"),
        {
          ...rateLimits,
          updatedAt: serverTimestamp(),
          updatedByUid: user?.uid ?? null,
        },
        { merge: true }
      );
      alert("Saved rate limit policy.");
    } catch (e: any) {
      console.error("[SettingsPage] saveRateLimits failed", e);
      setErr(e?.message || "Failed to save rate limit policy.");
    }
  }

  if (!user) {
    return (
      <div className="card">
        <h2>Settings</h2>
        <div className="muted">Sign in.</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="card">
        <h2>Settings</h2>
        <div className="muted">Admin only.</div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card">
        <h2>Settings</h2>
        <div className="muted">Settings are grouped below into General, Messaging, Volunteer, and System sections.</div>
        {loading ? <div className="muted" style={{ marginTop: 10 }}>Loading…</div> : null}
        {err ? <div style={{ marginTop: 10, color: "#dc2626", whiteSpace: "pre-wrap" }}>{err}</div> : null}
      </div>

      <div className="card section-card">
        <div className="section-eyebrow">Messaging</div>
        <h3>News Policy</h3>
        <div className="muted">Controls defaults and urgent expiry constraints.</div>
        <div style={{ display: "grid", gap: 10, marginTop: 12, maxWidth: 520 }}>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Default expire days
            <input className="input" type="number" min={1} max={365} value={news.defaultExpireDays} onChange={(e) => setNews({ ...news, defaultExpireDays: num(e.target.value, 7) })} />
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Urgent default hours
            <input className="input" type="number" min={1} max={168} value={news.urgentDefaultHours} onChange={(e) => setNews({ ...news, urgentDefaultHours: num(e.target.value, 24) })} />
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Urgent max hours
            <input className="input" type="number" min={1} max={720} value={news.urgentMaxHours} onChange={(e) => setNews({ ...news, urgentMaxHours: num(e.target.value, 72) })} />
          </label>
          <button className="btn" onClick={saveNews}>Save News Policy</button>
        </div>
      </div>

      <div className="card section-card">
        <div className="section-eyebrow">Messaging</div>
        <h3>Outside Staff Hours Auto-Reply</h3>
        <div className="muted">
          Parent-facing disclaimer for direct messages sent outside staff hours, weekends, and configured no-school calendar days.
        </div>
        <div style={{ display: "grid", gap: 14, marginTop: 12, maxWidth: 980 }}>
          <label className="messaging-pref" style={{ justifyContent: "flex-start" }}>
            <input
              type="checkbox"
              checked={messagingAutoReply.enabled}
              onChange={(e) => setMessagingAutoReply((prev) => ({ ...prev, enabled: e.currentTarget.checked }))}
              disabled={!isAdmin && !isSuperAdmin}
            />
            <span>
              <strong>Enable auto-reply</strong>
              <span className="muted">One system reply per thread per continuous off-hours window.</span>
            </span>
          </label>

          <label className="messaging-pref" style={{ justifyContent: "flex-start" }}>
            <input
              type="checkbox"
              checked={messagingAutoReply.showPresendDisclaimer}
              onChange={(e) => setMessagingAutoReply((prev) => ({ ...prev, showPresendDisclaimer: e.currentTarget.checked }))}
              disabled={!isAdmin && !isSuperAdmin}
            />
            <span>
              <strong>Show pre-send disclaimer in mobile app</strong>
              <span className="muted">Staged for the next iOS and Android app build. Text remains remote-editable after release.</span>
            </span>
          </label>

          <label className="messaging-pref" style={{ justifyContent: "flex-start" }}>
            <input
              type="checkbox"
              checked={messagingAutoReply.respectNoSchoolCalendar}
              onChange={(e) => setMessagingAutoReply((prev) => ({ ...prev, respectNoSchoolCalendar: e.currentTarget.checked }))}
              disabled={!isAdmin && !isSuperAdmin}
            />
            <span>
              <strong>Use calendar closure days</strong>
              <span className="muted">Full-day events matching the keywords below count as no-school days.</span>
            </span>
          </label>

          <div className="grid2" style={{ maxWidth: 760 }}>
            <label className="muted" style={{ display: "grid", gap: 6 }}>
              Timezone
              <input
                className="input"
                value={messagingAutoReply.timezone}
                onChange={(e) => setMessagingAutoReply((prev) => ({ ...prev, timezone: e.target.value }))}
                disabled={!isAdmin && !isSuperAdmin}
                placeholder="America/Chicago"
              />
            </label>

            <label className="muted" style={{ display: "grid", gap: 6 }}>
              Cooldown behavior
              <input className="input" value="One reply per off-hours window" disabled />
            </label>
          </div>

          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Disclaimer text
            <textarea
              className="input"
              rows={5}
              value={messagingAutoReply.messageText}
              onChange={(e) => setMessagingAutoReply((prev) => ({ ...prev, messageText: e.target.value }))}
              disabled={!isAdmin && !isSuperAdmin}
            />
          </label>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontWeight: 700 }}>Thread Types</div>
            <div className="muted">Auto-replies remain parent-triggered only. Direct messages are enabled by default.</div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {[
                ["direct", "Direct Messages"],
                ["club", "Club Chats"],
                ["broadcast", "Announcements"],
              ].map(([value, label]) => (
                <label key={value} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={messagingAutoReply.enabledThreadTypes.includes(value)}
                    onChange={(e) => toggleMessagingAutoReplyThreadType(value, e.currentTarget.checked)}
                    disabled={!isAdmin && !isSuperAdmin}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontWeight: 700 }}>Staff Hours</div>
            <div className="muted">Enabled days use the start and end times below. Disabled days are treated as off-hours.</div>
            {([
              ["mon", "Monday"],
              ["tue", "Tuesday"],
              ["wed", "Wednesday"],
              ["thu", "Thursday"],
              ["fri", "Friday"],
              ["sat", "Saturday"],
              ["sun", "Sunday"],
            ] as Array<[keyof MessagingAutoReplySettings["schedule"], string]>).map(([day, label]) => (
              <div key={day} style={{ display: "grid", gridTemplateColumns: "150px 1fr 1fr", gap: 10, alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={messagingAutoReply.schedule[day].enabled}
                    onChange={(e) => updateMessagingAutoReplyDay(day, { enabled: e.currentTarget.checked })}
                    disabled={!isAdmin && !isSuperAdmin}
                  />
                  <span>{label}</span>
                </label>
                <input
                  className="input"
                  type="time"
                  value={messagingAutoReply.schedule[day].start}
                  onChange={(e) => updateMessagingAutoReplyDay(day, { start: e.target.value })}
                  disabled={!isAdmin && !isSuperAdmin}
                />
                <input
                  className="input"
                  type="time"
                  value={messagingAutoReply.schedule[day].end}
                  onChange={(e) => updateMessagingAutoReplyDay(day, { end: e.target.value })}
                  disabled={!isAdmin && !isSuperAdmin}
                />
              </div>
            ))}
          </div>

          <SimpleListEditor
            title="No-School Calendar Keywords"
            description="Matched against full-day calendar event titles and descriptions. Half-days and in-service days are ignored unless their full-day event text matches one of these keywords."
            values={messagingAutoReply.noSchoolKeywords}
            onChange={(noSchoolKeywords) => setMessagingAutoReply((prev) => ({ ...prev, noSchoolKeywords }))}
            addLabel="Add Keyword"
            placeholder="no school"
            quickAdds={[
              { label: "No School", value: "no school" },
              { label: "Holiday", value: "holiday" },
              { label: "Snow Day", value: "snow day" },
            ]}
          />

          <button className="btn" onClick={saveMessagingAutoReplySettings} disabled={!isAdmin && !isSuperAdmin}>
            Save Messaging Auto-Reply
          </button>
          {(!isAdmin && !isSuperAdmin) ? <div className="muted">Only Admin or Super Admin can change this setting.</div> : null}
        </div>
      </div>

      <OfficeMessagingSettings />

      <div className="card section-card">
        <div className="section-eyebrow">General</div>
        <h3>Parent-to-Principal Messaging</h3>
        <div className="muted">
          Uses the Principal special assignment. Parents see the principal for a student only when this is enabled and that student has the matching special assignment.
        </div>
        <div style={{ display: "grid", gap: 14, marginTop: 12, maxWidth: 920 }}>
          <label className="messaging-pref" style={{ justifyContent: "flex-start" }}>
            <input
              type="checkbox"
              checked={parentPrincipalMessaging.enabled}
              onChange={(e) => setParentPrincipalMessaging((prev) => ({ ...prev, enabled: e.currentTarget.checked }))}
              disabled={!isAdmin && !isSuperAdmin}
            />
            <span>
              <strong>Allow parents to message the principal</strong>
              <span className="muted">New conversations are blocked when this is off. Existing threads are left intact.</span>
            </span>
          </label>

          <label className="muted" style={{ display: "grid", gap: 6, maxWidth: 420 }}>
            Principal special key
            <input
              className="input"
              value={parentPrincipalMessaging.principalSpecialKey}
              onChange={(e) => setParentPrincipalMessaging((prev) => ({ ...prev, principalSpecialKey: e.target.value }))}
              disabled={!isAdmin && !isSuperAdmin}
              placeholder="principal"
            />
          </label>

          <SimpleListEditor
            title="Blocked Family IDs"
            description="Families listed here cannot start new parent-to-principal conversations, even if their students have the Principal special assignment."
            values={parentPrincipalMessaging.blockedFamilyIds}
            onChange={(blockedFamilyIds) => setParentPrincipalMessaging((prev) => ({ ...prev, blockedFamilyIds }))}
            addLabel="Add Family ID"
            placeholder="F1001"
          />

          <SimpleListEditor
            title="Blocked Student Keys"
            description="Students listed here are excluded from new parent-to-principal conversations."
            values={parentPrincipalMessaging.blockedStudentKeys}
            onChange={(blockedStudentKeys) => setParentPrincipalMessaging((prev) => ({ ...prev, blockedStudentKeys }))}
            addLabel="Add Student Key"
            placeholder="51001"
          />

          <button className="btn" onClick={saveParentPrincipalMessagingSettings} disabled={!isAdmin && !isSuperAdmin}>
            Save Parent-to-Principal Messaging
          </button>
          {(!isAdmin && !isSuperAdmin) ? <div className="muted">Only Admin or Super Admin can change this setting.</div> : null}
        </div>
      </div>

      <div className="card section-card">
        <div className="section-eyebrow">General</div>
        <h3>Feature Availability</h3>
        <div className="muted">Use these switches to temporarily disable modules that are not ready for staff use.</div>
        <div style={{ display: "grid", gap: 12, marginTop: 12, maxWidth: 720 }}>
          <label className="messaging-pref" style={{ justifyContent: "flex-start" }}>
            <input
              type="checkbox"
              checked={featureFlags.familyCrmEnabled}
              disabled={!isAdmin && !isSuperAdmin}
              onChange={(e) => setFeatureFlags((prev) => ({ ...prev, familyCrmEnabled: e.currentTarget.checked }))}
            />
            <span>
              <strong>Family CRM enabled</strong>
              <span className="muted">When off, Family CRM navigation, route access, and dashboard cards are disabled.</span>
            </span>
          </label>
          <button className="btn" onClick={saveFeatureFlags} disabled={!isAdmin && !isSuperAdmin}>
            Save Feature Availability
          </button>
          {(!isAdmin && !isSuperAdmin) ? <div className="muted">Only Admin or Super Admin can change these settings.</div> : null}
        </div>
      </div>

      <div className="card section-card">
        <div className="section-eyebrow">General</div>
        <h3>Roster Options</h3>
        <div className="muted">Defines the controlled grade list and special programs used throughout roster and messaging screens.</div>
        <div style={{ display: "grid", gap: 18, marginTop: 12, maxWidth: 920 }}>
          <RosterOptionsEditor
            title="Grade Levels"
            description="Students use one grade. Teachers/staff can be assigned to one or more grades."
            values={rosterOptions.gradeLevels}
            onChange={(gradeLevels) => setRosterOptions((prev) => ({ ...prev, gradeLevels }))}
            addLabel="Add Grade"
          />
          <RosterOptionsEditor
            title="Special Programs"
            description="Used for extras like Art, Music, Band, PE, and electives."
            values={rosterOptions.specialPrograms}
            onChange={(specialPrograms) => setRosterOptions((prev) => ({ ...prev, specialPrograms }))}
            addLabel="Add Program"
          />
          <button className="btn" onClick={saveRosterOptions}>Save Roster Options</button>
        </div>
      </div>

      <div className="card">
        <h3>Calendar Integration</h3>
        <div className="muted">Controls which calendar feed the app uses at runtime. The app will read settings/calendar first and fall back to the app.json calendarApiUrl if this is blank.</div>
        <div style={{ display: "grid", gap: 10, marginTop: 12, maxWidth: 920 }}>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Calendar API URL
            <input
              className="input"
              value={calendarSettings.calendarApiUrl}
              onChange={(e) => setCalendarSettings((prev) => ({ ...prev, calendarApiUrl: e.target.value }))}
              placeholder="https://script.google.com/macros/s/.../exec"
            />
            <span className="muted">Use the Apps Script / feed endpoint the app should call for calendar events.</span>
          </label>

          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Google Calendar ID
            <input
              className="input"
              value={calendarSettings.googleCalendarId}
              onChange={(e) => setCalendarSettings((prev) => ({ ...prev, googleCalendarId: e.target.value }))}
              placeholder="schoolname@group.calendar.google.com"
            />
          </label>

          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Public Calendar URL
            <input
              className="input"
              value={calendarSettings.publicCalendarUrl}
              onChange={(e) => setCalendarSettings((prev) => ({ ...prev, publicCalendarUrl: e.target.value }))}
              placeholder="https://calendar.google.com/calendar/u/0?cid=..."
            />
          </label>

          <button className="btn" onClick={saveCalendarSettings}>Save Calendar Settings</button>
        </div>
      </div>

      <div className="card section-card">
        <div className="section-eyebrow">Volunteer</div>
        <h3>Retention Settings</h3>
        <div className="muted">These values are stored in settings/schoolYear.retentionYears and used for archival / purge planning across messaging, news, volunteer, and attachments.</div>
        <div className="grid2" style={{ marginTop: 12, maxWidth: 920 }}>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Messaging retention (years)
            <input className="input" type="number" min={1} max={25} value={retentionSettings.messaging} onChange={(e) => setRetentionSettings((prev) => ({ ...prev, messaging: clampYears(e.target.value, prev.messaging) }))} />
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            News retention (years)
            <input className="input" type="number" min={1} max={25} value={retentionSettings.news} onChange={(e) => setRetentionSettings((prev) => ({ ...prev, news: clampYears(e.target.value, prev.news) }))} />
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Volunteer retention (years)
            <input className="input" type="number" min={1} max={25} value={retentionSettings.volunteer} onChange={(e) => setRetentionSettings((prev) => ({ ...prev, volunteer: clampYears(e.target.value, prev.volunteer) }))} />
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Messaging attachments retention (years)
            <input className="input" type="number" min={1} max={25} value={retentionSettings.messagingAttachments} onChange={(e) => setRetentionSettings((prev) => ({ ...prev, messagingAttachments: clampYears(e.target.value, prev.messagingAttachments) }))} />
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            News attachments retention (years)
            <input className="input" type="number" min={1} max={25} value={retentionSettings.newsAttachments} onChange={(e) => setRetentionSettings((prev) => ({ ...prev, newsAttachments: clampYears(e.target.value, prev.newsAttachments) }))} />
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <button className="btn" onClick={saveRetentionSettings}>Save Retention Settings</button>
        </div>
      </div>


      <div className="card">
        <h3>Attachment Policy</h3>
        <div className="muted">Shared policy used by News and Messaging clients.</div>
        <div style={{ display: "grid", gap: 14, marginTop: 12, maxWidth: 920 }}>
          <label className="muted" style={{ display: "grid", gap: 6, maxWidth: 520 }}>
            Max bytes per file
            <input className="input" type="number" min={256 * 1024} max={50 * 1024 * 1024} value={attach.maxBytes} onChange={(e) => setAttach({ ...attach, maxBytes: num(e.target.value, DEFAULT_ATTACH.maxBytes) })} />
            <span className="muted">Current: {(attach.maxBytes / 1024 / 1024).toFixed(2)} MB</span>
          </label>
          <div className="grid2" style={{ maxWidth: 720 }}>
            <label className="muted" style={{ display: "grid", gap: 6 }}>
              Max files (News)
              <input className="input" type="number" min={0} max={10} value={attach.maxFilesNews} onChange={(e) => setAttach({ ...attach, maxFilesNews: num(e.target.value, 3) })} />
            </label>
            <label className="muted" style={{ display: "grid", gap: 6 }}>
              Max files (Messaging)
              <input className="input" type="number" min={0} max={10} value={attach.maxFilesMessaging} onChange={(e) => setAttach({ ...attach, maxFilesMessaging: num(e.target.value, 3) })} />
            </label>
          </div>
          <SimpleListEditor
            title="Allowed MIME Types"
            description="Use one entry per MIME type. This is easier to maintain than a comma-separated field."
            values={attach.allowedMimeTypes}
            onChange={(allowedMimeTypes) => setAttach((prev) => ({ ...prev, allowedMimeTypes }))}
            addLabel="Add MIME Type"
            placeholder="application/pdf"
            quickAdds={COMMON_MIME_TYPES}
          />
          <button className="btn" onClick={saveAttach}>Save Attachment Policy</button>
        </div>
      </div>

      <div className="card">
        <h3>Public Website Media Limits</h3>
        <div className="muted">
          Super-admin-only limits used by the public website CMS uploaders. Storage rules keep these values within hard safety ceilings.
        </div>
        <div className="grid2" style={{ marginTop: 12, maxWidth: 920 }}>
          {[
            ["maxHeroImageBytes", "Hero image max"],
            ["maxThemeImageBytes", "Theme/logo image max"],
            ["maxStaffPhotoBytes", "Staff photo max"],
            ["maxNewsletterPdfBytes", "Newsletter PDF max"],
            ["maxPublicVideoBytes", "Public video max"],
          ].map(([key, label]) => {
            const field = key as keyof PublicWebMediaPolicy;
            return (
              <label key={key} className="muted" style={{ display: "grid", gap: 6 }}>
                {label}
                <input
                  className="input"
                  type="number"
                  min={0.25 * 1024 * 1024}
                  step={1024 * 1024}
                  value={publicWebMediaPolicy[field]}
                  disabled={!isSuperAdmin}
                  onChange={(e) => setPublicWebMediaPolicy((prev) => ({ ...prev, [field]: num(e.target.value, DEFAULT_PUBLIC_WEB_MEDIA_POLICY[field]) }))}
                />
                <span className="muted">Current: {formatBytesMb(publicWebMediaPolicy[field])}</span>
              </label>
            );
          })}
        </div>
        <div style={{ marginTop: 12 }}>
          <button className="btn" onClick={savePublicWebMediaPolicy} disabled={!isSuperAdmin}>Save Public Website Media Limits</button>
          {!isSuperAdmin ? <div className="muted" style={{ marginTop: 8 }}>Only Super Admin can change these upload limits.</div> : null}
        </div>
      </div>

      <div className="card">
        <h3>Email Branding</h3>
        <div className="muted">Used for invites, account setup, and system-generated emails. Fonts are limited to email-safe families for better cross-client consistency.</div>
        <div style={{ display: "grid", gap: 18, marginTop: 12 }}>
          <div className="grid2" style={{ alignItems: "start", gap: 18 }}>
            <div style={{ display: "grid", gap: 10, maxWidth: 720 }}>
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                From Name
                <input className="input" value={emailBranding.fromName} onChange={(e) => setEmailBranding({ ...emailBranding, fromName: e.target.value })} />
              </label>
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                From Email
                <input className="input" value={emailBranding.fromEmail} onChange={(e) => setEmailBranding({ ...emailBranding, fromEmail: e.target.value })} />
              </label>
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                Reply-To Email
                <input className="input" value={emailBranding.replyToEmail} onChange={(e) => setEmailBranding({ ...emailBranding, replyToEmail: e.target.value })} />
              </label>
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                Activation Base URL
                <input className="input" value={emailBranding.activationBaseUrl} onChange={(e) => setEmailBranding({ ...emailBranding, activationBaseUrl: e.target.value })} placeholder={adminBaseUrlPlaceholder || "https://admin.zionclassical.org"} />
              </label>
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                Admin Web Base URL
                <input className="input" value={emailBranding.adminWebBaseUrl} onChange={(e) => setEmailBranding({ ...emailBranding, adminWebBaseUrl: e.target.value })} placeholder={adminBaseUrlPlaceholder || "https://admin.zionclassical.org"} />
              </label>
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                School Name
                <input className="input" value={emailBranding.schoolName} onChange={(e) => setEmailBranding({ ...emailBranding, schoolName: e.target.value })} />
              </label>
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                App Name
                <input className="input" value={emailBranding.appName} onChange={(e) => setEmailBranding({ ...emailBranding, appName: e.target.value })} />
              </label>
              <div style={{ display: "grid", gap: 8 }}>
                <div className="muted" style={{ fontWeight: 600 }}>Logo Upload</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void uploadEmailLogo(file);
                      e.currentTarget.value = "";
                    }}
                  />
                  <button
                    type="button"
                    className="btn secondary"
                    disabled={logoUploading}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {logoUploading ? "Uploading Logo..." : "Choose Logo"}
                  </button>
                  <span className="muted">PNG preferred. Max 2 MB.</span>
                </div>
                <div className="muted">This stores the logo in Firebase Storage instead of requiring a pasted URL.</div>
                {logoUiError ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{logoUiError}</div> : null}
                {logoUiSuccess ? <div style={{ color: "#166534", fontSize: 13 }}>{logoUiSuccess}</div> : null}
                {logoLocalPreviewUrl || emailBranding.logoDownloadUrl || emailBranding.logoUrl ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    <img
                      src={logoLocalPreviewUrl || emailBranding.logoDownloadUrl || emailBranding.logoUrl}
                      alt="Email logo preview"
                      style={{ maxWidth: 220, maxHeight: 72, objectFit: "contain", padding: 8, border: "1px solid #e5e7eb", borderRadius: 12, background: "#fff" }}
                    />
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button type="button" className="btn secondary" onClick={() => void removeEmailLogo()}>Remove Logo</button>
                      {emailBranding.logoStoragePath ? <span className="muted">Stored at: {emailBranding.logoStoragePath}</span> : null}
                    </div>
                  </div>
                ) : (
                  <div className="muted">No logo uploaded.</div>
                )}
              </div>
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                Primary Color
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 10, alignItems: "center" }}>
                  <input className="input" type="color" value={emailBranding.primaryColor} onChange={(e) => setEmailBranding({ ...emailBranding, primaryColor: e.target.value })} />
                  <input className="input" value={emailBranding.primaryColor} onChange={(e) => setEmailBranding({ ...emailBranding, primaryColor: e.target.value })} />
                </div>
              </label>
              <div className="grid2">
                <label className="muted" style={{ display: "grid", gap: 6 }}>
                  Body Font
                  <select className="input" value={emailBranding.bodyFontFamily} onChange={(e) => setEmailBranding({ ...emailBranding, bodyFontFamily: e.target.value })}>
                    {EMAIL_FONT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <label className="muted" style={{ display: "grid", gap: 6 }}>
                  Heading Font
                  <select className="input" value={emailBranding.headingFontFamily} onChange={(e) => setEmailBranding({ ...emailBranding, headingFontFamily: e.target.value })}>
                    {EMAIL_FONT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
              </div>
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                Footer Text
                <textarea className="input" rows={4} value={emailBranding.footerText} onChange={(e) => setEmailBranding({ ...emailBranding, footerText: e.target.value })} />
              </label>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button className="btn" disabled={emailSaving || logoUploading} onClick={() => void saveEmailBranding()}>{emailSaving ? "Saving..." : "Save Email Branding"}</button>
                <button className="btn secondary" disabled={previewLoading || logoUploading} onClick={() => void refreshEmailPreview()}>{previewLoading ? "Generating Preview..." : "Refresh Preview"}</button>
              </div>
              {(logoUploading || previewLoading) ? <div className="muted">{logoUploading ? "Uploading logo..." : "Generating preview..."}</div> : null}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontWeight: 700 }}>Email Preview</div>
              <div className="muted">Current preview uses the account setup template, which is the live system email template currently used by invites.</div>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden", background: "#fff" }}>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid #e5e7eb", background: "#f8fafc" }}>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Subject</div>
                  <div style={{ fontWeight: 700 }}>{previewSubject || `${emailBranding.schoolName} Account Setup`}</div>
                </div>
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml || "<div style='padding:16px;font-family:Arial,Helvetica,sans-serif;'>Preview unavailable.</div>"}
                  style={{ width: "100%", minHeight: 720, border: "0", background: "#fff" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card section-card">
        <div className="section-eyebrow">System</div>
        <h3>Session Policy</h3>
        <div className="muted">Super Admin only. Controls idle logout timing and trusted network session extension.</div>
        <div style={{ display: "grid", gap: 12, marginTop: 12, maxWidth: 920 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={sessionPolicy.enableTrustedTeacherSessionExtension}
              onChange={(e) => setSessionPolicy((prev) => ({ ...prev, enableTrustedTeacherSessionExtension: e.target.checked }))}
              disabled={!isSuperAdmin}
            />
            <span>Allow extended sessions on trusted school public IPs</span>
          </label>

          <div className="grid2" style={{ maxWidth: 720 }}>
            <label className="muted" style={{ display: "grid", gap: 6 }}>
              Default idle timeout (minutes)
              <input
                className="input"
                type="number"
                min={1}
                max={1440}
                value={sessionPolicy.defaultIdleTimeoutMinutes}
                onChange={(e) => setSessionPolicy((prev) => ({ ...prev, defaultIdleTimeoutMinutes: Math.max(1, Math.min(1440, num(e.target.value, prev.defaultIdleTimeoutMinutes))) }))}
                disabled={!isSuperAdmin}
              />
            </label>

            <label className="muted" style={{ display: "grid", gap: 6 }}>
              Trusted network idle timeout (minutes)
              <input
                className="input"
                type="number"
                min={1}
                max={1440}
                value={sessionPolicy.trustedTeacherIdleTimeoutMinutes}
                onChange={(e) => setSessionPolicy((prev) => ({ ...prev, trustedTeacherIdleTimeoutMinutes: Math.max(1, Math.min(1440, num(e.target.value, prev.trustedTeacherIdleTimeoutMinutes))) }))}
                disabled={!isSuperAdmin}
              />
            </label>
          </div>

          <SimpleListEditor
            title="Trusted Public IP Addresses"
            description="Enter one school public WAN IP per line item. Exact matches only. Users on these IPs may receive the extended timeout when enabled."
            values={sessionPolicy.trustedNetworkIps}
            onChange={(trustedNetworkIps) => setSessionPolicy((prev) => ({ ...prev, trustedNetworkIps }))}
            addLabel="Add Trusted IP"
            placeholder="203.0.113.10"
          />

          <button className="btn" onClick={saveSessionPolicy} disabled={!isSuperAdmin}>Save Session Policy</button>
          {!isSuperAdmin ? <div className="muted">Only Super Admin can change session policy.</div> : null}
        </div>
      </div>

      <div className="card section-card">
        <div className="section-eyebrow">Roster Import</div>
        <h3>Import Sequence Pools</h3>
        <div className="muted">Admin or Super Admin can control the starting values the system uses when assigning new Parent IDs and Family IDs during import or manual creation.</div>
        <div className="grid2" style={{ marginTop: 12, maxWidth: 720 }}>
          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Next parent number (P####)
            <input
              className="input"
              type="number"
              min={1}
              value={importSequences.nextParentNumber}
              onChange={(e) => setImportSequences((prev) => ({ ...prev, nextParentNumber: Math.max(1, Math.floor(num(e.target.value, prev.nextParentNumber))) }))}
            />
          </label>

          <label className="muted" style={{ display: "grid", gap: 6 }}>
            Next family number (F####)
            <input
              className="input"
              type="number"
              min={1}
              value={importSequences.nextFamilyNumber}
              onChange={(e) => setImportSequences((prev) => ({ ...prev, nextFamilyNumber: Math.max(1, Math.floor(num(e.target.value, prev.nextFamilyNumber))) }))}
            />
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <button className="btn" onClick={saveImportSequences} disabled={!isAdmin && !isSuperAdmin}>Save Import Sequence Pools</button>
          {(!isAdmin && !isSuperAdmin) ? <div className="muted" style={{ marginTop: 8 }}>Only Admin or Super Admin can change these settings.</div> : null}
        </div>
      </div>

      <div className="card">
        <h3>Rate Limit Policy</h3>
        <div className="muted">Super Admin only. Controls administrative abuse limits for invite, linking, public volunteer entry, and destructive actions.</div>
        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          <RateLimitEditor label="Interactive invite per actor" value={rateLimits.interactiveInvitePerActor} onChange={(next) => setRateLimits((prev) => ({ ...prev, interactiveInvitePerActor: next }))} />
          <RateLimitEditor label="Interactive invite per target email" value={rateLimits.interactiveInvitePerTargetEmail} onChange={(next) => setRateLimits((prev) => ({ ...prev, interactiveInvitePerTargetEmail: next }))} />
          <RateLimitEditor label="Password setup link per actor" value={rateLimits.passwordSetupLinkPerActor} onChange={(next) => setRateLimits((prev) => ({ ...prev, passwordSetupLinkPerActor: next }))} />
          <RateLimitEditor label="Password setup link per target email" value={rateLimits.passwordSetupLinkPerTargetEmail} onChange={(next) => setRateLimits((prev) => ({ ...prev, passwordSetupLinkPerTargetEmail: next }))} />
          <RateLimitEditor label="Link account per actor" value={rateLimits.linkAccountPerActor} onChange={(next) => setRateLimits((prev) => ({ ...prev, linkAccountPerActor: next }))} />
          <RateLimitEditor label="Role change per actor" value={rateLimits.roleChangePerActor} onChange={(next) => setRateLimits((prev) => ({ ...prev, roleChangePerActor: next }))} />
          <RateLimitEditor label="Delete user per actor" value={rateLimits.deleteUserPerActor} onChange={(next) => setRateLimits((prev) => ({ ...prev, deleteUserPerActor: next }))} />
          <RateLimitEditor label="Public volunteer entry per family code" value={rateLimits.publicVolunteerEntryPerFamilyCode} onChange={(next) => setRateLimits((prev) => ({ ...prev, publicVolunteerEntryPerFamilyCode: next }))} />
          <RateLimitEditor label="Public volunteer entry per email" value={rateLimits.publicVolunteerEntryPerEmail} onChange={(next) => setRateLimits((prev) => ({ ...prev, publicVolunteerEntryPerEmail: next }))} />
          <button className="btn" onClick={saveRateLimits} disabled={!isSuperAdmin}>Save Rate Limit Policy</button>
          {!isSuperAdmin ? <div className="muted">Only Super Admin can change rate limits.</div> : null}
        </div>
      </div>
    </div>
  );
}
